const text = (element) => String(element?.value ?? "").trim();

const hasPayload = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") {
    return Object.values(value).some((item) => {
      if (typeof item === "boolean") return item;
      if (Array.isArray(item)) return item.length > 0;
      return item !== undefined && item !== null && String(item).trim() !== "";
    });
  }
  return value !== undefined && value !== null && String(value).trim() !== "";
};

const collectChangedFields = (root, isChanged) => {
  const fields = Array.from(root?.querySelectorAll("input:not([type='file']), textarea, select") || [])
    .filter(isChanged)
    .map((field) => ({
      label: field.getAttribute("aria-label") || field.getAttribute("placeholder") || field.closest("label")?.querySelector("span")?.textContent.trim() || field.name || field.type || "field",
      value: field instanceof HTMLInputElement && ["checkbox", "radio"].includes(field.type)
        ? field.checked
        : text(field),
    }));
  const controls = Array.from(root?.querySelectorAll("button[aria-pressed], button[role='switch']") || [])
    .filter(isChanged)
    .map((button) => ({
      label: button.getAttribute("aria-label") || button.textContent.trim(),
      value: button.getAttribute("role") === "switch"
        ? button.getAttribute("aria-checked") === "true"
        : button.getAttribute("aria-pressed") === "true",
    }));
  return [...fields, ...controls];
};

export function collectNephrologySubmission(page, isChanged) {
  const section = (id) => page?.querySelector(`#${id}-content`);
  const historyRoot = section("reason-history");
  const examRoot = section("vitals-exam");
  const urineRoot = section("urine-uremia");
  const imagingRoot = section("renal-imaging");
  const biologyRoot = section("biology-ckd");
  const nephroRoot = section("nephroprotection-rrt");
  const summaryRoot = section("summary-plan");

  const selectedReasonButtons = Array.from(historyRoot?.querySelectorAll(".reason-history__chip[aria-pressed='true']") || []);
  const reasonButtonsChanged = Array.from(historyRoot?.querySelectorAll(".reason-history__chip") || [])
    .some((button) => isChanged(button) || (
      button.dataset.customReason === "true" &&
      button.getAttribute("aria-pressed") === "true"
    ));
  const patientHistoryInput = historyRoot?.querySelector(".reason-history__textarea");
  const selectedReasonLabels = selectedReasonButtons
    .map((button) => button.textContent.trim())
    .filter(Boolean);
  const patientClinicalExamination = text(patientHistoryInput);
  const reasonSummary = selectedReasonLabels.join(", ");
  const history = {
    ...(reasonButtonsChanged && {
      diseaseHistory: [
        ...selectedReasonButtons.map((button) => button.dataset.reason || button.textContent.trim()),
      ].map((reason) => reason.trim()).filter(Boolean).join("\n"),
    }),
    ...(isChanged(patientHistoryInput) && {
      patientClinicalExamination,
    }),
  };
  const historyDescription = [
    reasonSummary
      ? `${historyRoot?.querySelector(".reason-history__label")?.textContent.trim() || "Motif"} : ${reasonSummary}`
      : "",
    patientClinicalExamination
      ? `${historyRoot?.querySelector("label[for='patient-exam']")?.textContent.trim() || "Antécédents et examen clinique"} : ${patientClinicalExamination}`
      : "",
  ].filter(Boolean).join(" · ");

  const exam = {};
  const examValidationErrors = [];
  examRoot?.querySelectorAll("[data-exam-field]:not([data-exam-field='omi'])").forEach((field) => {
    if (!isChanged(field)) return;
    const key = field.dataset.examField;
    const value = text(field);
    if (!value) return;

    if (key === "glasgowScore") {
      const score = Number(value);
      if (!Number.isInteger(score) || score < 3 || score > 15) {
        examValidationErrors.push("Le score de Glasgow doit être un nombre entier compris entre 3 et 15.");
        return;
      }
      exam.glasgowScore = score;
      return;
    }

    exam[key] = value;
  });
  const selectedEdema = examRoot?.querySelector(".vitals-exam__edema-option[aria-pressed='true']");
  if (isChanged(selectedEdema) && selectedEdema?.dataset.value) {
    exam.omi = selectedEdema.dataset.value;
  }

  const selectedUrineValues = (selector) =>
    Array.from(urineRoot?.querySelectorAll(`${selector}[aria-pressed="true"]`) || [])
      .map((button) => button.dataset.value || button.textContent.trim())
      .filter(Boolean);
  const symptomChips = selectedUrineValues(".urine-uremia__block:first-child .urine-uremia__chip");
  const uremicSigns = selectedUrineValues(".urine-uremia__signs .urine-uremia__chip");
  const bu = {};
  const validationErrors = [...examValidationErrors];
  const buKeys = ["hematuria", "ketones", "glucose", "proteinuria", "leukocyturia"];
  Array.from(urineRoot?.querySelectorAll(".urine-uremia__table tbody tr") || []).slice(0, 5).forEach((row, index) => {
    const active = row.querySelector("button.is-active");
    if (!active) return;
    bu[buKeys[index]] = active.classList.contains("is-active--negative") ? "neg" : active.textContent.trim();
  });
  const selectedBuCount = Array.from(urineRoot?.querySelectorAll(".urine-uremia__table tbody tr") || [])
    .slice(0, 5)
    .filter((row) => row.querySelector("button.is-active")).length;
  const phInput = urineRoot?.querySelector(".urine-uremia__ph input");
  const phValue = text(phInput);
  if (selectedBuCount > 0 && (selectedBuCount !== buKeys.length || !phValue)) {
    validationErrors.push("Sélectionnez un résultat pour chaque paramètre de la bandelette et indiquez le pH.");
  }
  const glycemiaValue = text(urineRoot?.querySelector(".urine-uremia__glycemia input"));
  const urine = {
    urinarySymptoms: symptomChips,
    bm: text(urineRoot?.querySelector(".urine-uremia__bowel input")),
    bu: Object.keys(bu).length === buKeys.length && phValue ? {
      ...bu,
      ph: Number(phValue),
    } : undefined,
    uremicSigns,
    glycemia: glycemiaValue
      ? Math.round(Number(glycemiaValue))
      : undefined,
  };

  const biologicalParameters = Array.from(biologyRoot?.querySelectorAll(".biology-ckd__table tbody tr") || []).map((row) => {
    const cells = Array.from(row.cells || []);
    const periodHeaders = Array.from(
      biologyRoot?.querySelectorAll(".biology-ckd__table thead th[data-period-id]") || [],
    );
    const periods = periodHeaders.map((header) => header.dataset.periodId || "");
    const valueInputs = cells
      .filter((cell) => cell.dataset.periodId)
      .map((cell) => cell.querySelector("input"));
    const values = valueInputs.map((input) => text(input));
    if (!values.some(Boolean)) return null;
    const trendClass = row.querySelector(".biology-ckd__sparkline")?.getAttribute("class") || "";
    return {
      parameter: row.dataset.biologyParameter || row.querySelector("td strong")?.textContent.trim() || "",
      unit: row.querySelector("td small")?.textContent.trim() || undefined,
      m6: values[periods.indexOf("m6")] || undefined,
      m3: values[periods.indexOf("m3")] || undefined,
      current: values[periods.indexOf("current")] || undefined,
      periods,
      values,
      target: cells.at(-2)?.textContent.trim() || undefined,
      trend: trendClass.includes("--up") ? "up" : trendClass.includes("--down") ? "down" : "flat",
    };
  }).filter(Boolean);
  const extraBiology = Array.from(biologyRoot?.querySelectorAll(".biology-ckd__other-field[data-biology-parameter]") || []).map((field) => ({
    parameter: field.dataset.biologyParameter,
    unit: field.querySelector("i")?.textContent.trim() || undefined,
    current: text(field.querySelector("input")) || undefined,
  })).filter((item) => item.parameter && item.current);
  const ckdStage = text(biologyRoot?.querySelector(".biology-ckd__stage select"));
  const albuminuriaCategory = text(biologyRoot?.querySelector(".biology-ckd__stage select[aria-label]"));
  if (ckdStage) biologicalParameters.push({ parameter: "ckdStage", current: ckdStage });
  if (albuminuriaCategory) biologicalParameters.push({ parameter: "albuminuriaCategory", current: albuminuriaCategory });
  const esaSwitch = biologyRoot?.querySelector(".biology-ckd__esa");
  const esaChecked = esaSwitch?.getAttribute("aria-checked") === "true";
  const esaEnabled = esaChecked !== (esaSwitch?.dataset.loadedChecked === "true") || isChanged(esaSwitch)
    ? esaChecked
    : undefined;
  const epoEsaDate = text(biologyRoot?.querySelector(".biology-ckd__esa-date"));
  const biology = {
    biologicalParameters: [...biologicalParameters, ...extraBiology],
    ...((esaChecked || esaEnabled !== undefined) && { epoEsa: esaChecked }),
    ...(esaChecked && epoEsaDate && { epoEsaDate }),
    ...(esaEnabled === false && { epoEsaDate: null }),
  };

  const switches = Array.from(nephroRoot?.querySelectorAll(".nephro-rrt__protection-list [role='switch']") || []);
  const activeModality = nephroRoot?.querySelector(".nephro-rrt__modalities [aria-pressed='true']");
  const protectionKeys = ["bsra", "sglt", "finerenone"];
  const preparationKeys = ["dopplerMs", "fav", "capitauxVineux"];
  const selectedValues = (selector, attribute) =>
    Array.from(nephroRoot?.querySelectorAll(`${selector}:checked`) || [])
      .map((input) => input.dataset[attribute])
      .filter(Boolean);
  const nephroFields = Array.from(nephroRoot?.querySelectorAll("input, [aria-pressed], [role='switch']") || []);
  const nephroSectionChanged = nephroFields.some(isChanged);
  const doseInput = nephroRoot?.querySelector("[data-nephro-dose-adjustment]");
  const saltInput = nephroRoot?.querySelector("[data-nephro-diet='saltIntake']");
  const proteinInput = nephroRoot?.querySelector("[data-nephro-diet='proteinTarget']");
  const nephroprotection = nephroSectionChanged || nephroRoot?.dataset.nephroRecordId ? {
    ...Object.fromEntries(protectionKeys.map((key, index) => [
      key,
      switches[index]?.getAttribute("aria-checked") === "true",
    ])),
    ...Object.fromEntries(protectionKeys.map((key, index) => [
      `${key}Date`,
      switches[index]?.getAttribute("aria-checked") === "true"
        ? text(nephroRoot?.querySelector(`[data-nephro-date='${key}']`)) || null
        : null,
    ])),
    nephroAins: selectedValues("input[data-nephro-toxin]", "nephroToxin"),
    posologie: doseInput?.checked ? ["doseAdjustment"] : [],
    vaccination: selectedValues("input[data-nephro-vaccine]", "nephroVaccine"),
    presuppleance: activeModality?.dataset.nephroModality || null,
    ...Object.fromEntries(preparationKeys.map((key) => [
      key,
      nephroRoot?.querySelector(`[data-nephro-switch='${key}']`)?.getAttribute("aria-checked") === "true",
    ])),
    saltIntake: text(saltInput) ? Number(text(saltInput)) : null,
    proteinTarget: text(proteinInput) ? Number(text(proteinInput)) : null,
    medicalActs: Array.from(nephroRoot?.querySelectorAll("[data-nephro-procedure][aria-pressed='true']") || [])
      .map((button) => button.dataset.nephroProcedure),
  } : {};

  const summary = {
    diagnosis: text(summaryRoot?.querySelector(".summary-plan__diagnosis textarea")),
    summary: text(summaryRoot?.querySelector(".summary-plan__panel--summary > textarea")),
    plan: text(summaryRoot?.querySelector(".summary-plan__panel--plan > textarea")),
  };
  const summaryPayload = Object.values(summary).some(Boolean) || summaryRoot?.dataset.summaryRecordId
    ? { description: JSON.stringify(summary) }
    : {};
  const imaging = Array.from(imagingRoot?.querySelectorAll(".renal-imaging__card") || []).map((card) => ({
    imagingType: card.dataset.imagingType,
    file: card.querySelector("[data-imaging-file]")?.files?.[0] || null,
    documentId: text(card.querySelector("[data-imaging-document-id]")) || undefined,
    attachment: card.querySelector(".renal-imaging__file span:last-child")?.textContent.trim() || "",
    dateDescription: text(card.querySelector("[data-imaging-date]")) || undefined,
    description: text(card.querySelector("[data-imaging-report]")),
  })).filter((item) => item.file || item.documentId || item.dateDescription || item.description);

  if (esaChecked && !epoEsaDate) validationErrors.push("Indiquez la date de début du traitement EPO/ASE.");
  if (nephroprotection.bsra && !nephroprotection.bsraDate) validationErrors.push("Indiquez la date de début du traitement IEC/ARA2.");
  if (nephroprotection.sglt && !nephroprotection.sgltDate) validationErrors.push("Indiquez la date de début du traitement SGLT2.");
  if (nephroprotection.finerenone && !nephroprotection.finerenoneDate) validationErrors.push("Indiquez la date de début de la finérénone.");

  const payloads = {
    history,
    exam,
    "urinary-uremia": urine,
    "biological-follow-up": biology,
    nephroprotection,
    summary: summaryPayload,
  };
  const snapshotsWithFields = [
    ["Motif et antécédents", history, historyRoot],
    ["Examen neurologique", exam, examRoot],
    ["Urines et urémie", urine, urineRoot],
    ["Biologie rénale", biology, biologyRoot],
    ["Néphroprotection et suppléance", nephroprotection, nephroRoot],
    ["Imagerie rénale", imaging.map(({ file, ...item }) => item), imagingRoot],
    ["Synthèse et plan", summary, summaryRoot],
  ];
  const snapshots = snapshotsWithFields.map(([title, data, root]) => ({
    title,
    description: root === historyRoot
      ? historyDescription
      : JSON.stringify({ data, changedFields: collectChangedFields(root, isChanged) }),
  }));

  return {
    payloads: Object.fromEntries(Object.entries(payloads).filter(([, value]) => hasPayload(value))),
    imaging,
    summary,
    reasonSummary,
    validationErrors,
    consultationSections: snapshots,
  };
}
